import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { MdSearch, MdClose } from 'react-icons/md';
import PlotlyChart from '../../components/charts/PlotlyChart';
import MetricCard from '../../components/shared/MetricCard';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import { hfService } from '../../services/hfService';
import { formatPct, formatNum, parseDfJson } from '../../utils/formatters';
import { HEATMAP_RETURNS } from '../../components/charts/chartTheme';
import FundProfileSection from './FundProfileSection';
import FundNotesSection from './FundNotesSection';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const PERIOD_OPTIONS = [
  { label: '6M', months: 6 },
  { label: '1Y', months: 12 },
  { label: '2Y', months: 24 },
  { label: '3Y', months: 36 },
  { label: '5Y', months: 60 },
  { label: 'All', months: null },
];

const FundDetailPage = ({ analyticsParams, fundList, focusFund, cachedFetch }) => {
  const [selectedFund, setSelectedFund] = useState('');
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cumPeriod, setCumPeriod] = useState(null); // null = All

  // Default to focus fund from parent
  useEffect(() => {
    if (focusFund && !selectedFund) {
      setSelectedFund(focusFund);
    } else if (fundList?.length > 0 && !selectedFund) {
      setSelectedFund(fundList[0].display_name || fundList[0]);
    }
  }, [fundList, selectedFund, focusFund]);

  // Fetch fund detail
  const fetchDetail = useCallback(async () => {
    if (!analyticsParams?.dataset_id || !selectedFund) return;
    setLoading(true);
    setError(null);
    try {
      const fetchFn = () => hfService.getFundDetail({ ...analyticsParams, fund: selectedFund });
      const res = cachedFetch ? await cachedFetch('fund-detail-' + selectedFund, fetchFn) : await fetchFn();
      setDetail(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [analyticsParams, selectedFund]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const fundColor = detail?.color || '#0d6efd';

  // Resolve the selected fund's ID (needed for profile + notes endpoints)
  const selectedFundId = useMemo(() => {
    if (!selectedFund || !fundList) return null;
    const match = fundList.find(f => (f.display_name || f) === selectedFund);
    return match?.id ?? null;
  }, [fundList, selectedFund]);

  // Metric cards data
  const metricCards = useMemo(() => {
    if (!detail?.metrics) return [];
    const m = detail.metrics;
    const color = (v) => v > 0 ? 'text-green-600 dark:text-green-400' : v < 0 ? 'text-red-500 dark:text-red-400' : undefined;
    return [
      { label: 'Ann. Return', value: formatPct(m.ann_return), valueColor: color(m.ann_return) },
      { label: 'Ann. Std Dev', value: formatPct(m.ann_std) },
      { label: 'Sharpe', value: formatNum(m.sharpe), valueColor: color(m.sharpe) },
      { label: 'Max Drawdown', value: formatPct(m.max_drawdown), valueColor: 'text-red-500 dark:text-red-400' },
      { label: '% Pos Months', value: formatPct(m.pct_positive), valueColor: m.pct_positive > 0.5 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400' },
      { label: 'Best / Worst', value: `${formatPct(m.best_month)} / ${formatPct(m.worst_month)}` },
    ];
  }, [detail]);

  // Monthly returns bar chart — inception to latest only (no empty space)
  const monthlyBarTrace = useMemo(() => {
    if (!detail?.monthly_returns) return null;
    const { index, values } = detail.monthly_returns;
    const pairs = (index || []).map((d, i) => [new Date(d), values?.[i]])
      .filter(([, v]) => v != null);
    if (!pairs.length) return null;
    return {
      x: pairs.map(([d]) => d),
      y: pairs.map(([, v]) => v),
      type: 'bar',
      marker: {
        color: pairs.map(([, v]) => (v >= 0 ? '#28a745' : '#dc3545')),
      },
      hovertemplate: '%{x|%b %Y}: %{y:.2%}<extra></extra>',
    };
  }, [detail]);

  // Cumulative return line with SPX — filtered by period
  const cumulativeTraces = useMemo(() => {
    if (!detail?.cumulative) return [];
    const cum = parseDfJson(detail.cumulative);
    const traces = [];

    if (cum.index.length > 0) {
      let xDates = cum.index.map(d => new Date(d));
      let yVals = cum.data.map(row => (Array.isArray(row) ? row[0] : row));

      // Start from fund inception (first non-null value)
      const inceptionIdx = yVals.findIndex(v => v != null && !isNaN(v));
      if (inceptionIdx > 0) {
        xDates = xDates.slice(inceptionIdx);
        const baseVal = yVals[inceptionIdx];
        yVals = yVals.slice(inceptionIdx).map(v =>
          v != null ? (1 + v) / (1 + baseVal) - 1 : null
        );
      }

      // Filter by period and rebase from period start
      if (cumPeriod) {
        const cutoff = new Date(xDates[xDates.length - 1]);
        cutoff.setMonth(cutoff.getMonth() - cumPeriod);
        const startIdx = xDates.findIndex(d => d >= cutoff);
        if (startIdx > 0) {
          xDates = xDates.slice(startIdx);
          const baseVal = yVals[startIdx];
          yVals = yVals.slice(startIdx).map(v =>
            v != null && baseVal != null ? (1 + v) / (1 + baseVal) - 1 : null
          );
        }
      }

      traces.push({
        x: xDates,
        y: yVals,
        type: 'scatter',
        mode: 'lines',
        name: detail.fund || selectedFund,
        line: { color: fundColor, width: 2 },
        hovertemplate: '%{y:.2%}<extra></extra>',
      });
    }

    if (detail.spx_cumulative) {
      const spx = parseDfJson(detail.spx_cumulative);
      let spxDates = spx.index.map(d => new Date(d));
      let spxVals = spx.data.map(row => (Array.isArray(row) ? row[0] : row));

      // Clip SPX to fund inception — don't show before the fund existed
      const cum = parseDfJson(detail.cumulative);
      const fundFirstNonNull = cum.index.find((d, i) => {
        const v = Array.isArray(cum.data[i]) ? cum.data[i][0] : cum.data[i];
        return v != null;
      });
      if (fundFirstNonNull) {
        const fundStart = new Date(fundFirstNonNull);
        const clipIdx = spxDates.findIndex(d => d >= fundStart);
        if (clipIdx > 0) {
          spxDates = spxDates.slice(clipIdx);
          const baseVal = spxVals[clipIdx];
          spxVals = spxVals.slice(clipIdx).map(v =>
            v != null && baseVal != null ? (1 + v) / (1 + baseVal) - 1 : null
          );
        }
      }

      if (cumPeriod) {
        const cutoff = new Date(spxDates[spxDates.length - 1]);
        cutoff.setMonth(cutoff.getMonth() - cumPeriod);
        const startIdx = spxDates.findIndex(d => d >= cutoff);
        if (startIdx > 0) {
          spxDates = spxDates.slice(startIdx);
          const baseVal = spxVals[startIdx];
          spxVals = spxVals.slice(startIdx).map(v =>
            v != null && baseVal != null ? (1 + v) / (1 + baseVal) - 1 : null
          );
        }
      }

      // Filter nulls
      const pairs = spxDates.map((d, i) => [d, spxVals[i]]).filter(([, v]) => v != null);
      if (pairs.length > 0) {
        traces.push({
          x: pairs.map(([d]) => d),
          y: pairs.map(([, v]) => v),
          type: 'scatter',
          mode: 'lines',
          name: 'SPX Index',
          line: { color: '#DC143C', width: 2, dash: 'dash' },
          hovertemplate: 'SPX: %{y:.2%}<extra></extra>',
        });
      }
    }

    return traces;
  }, [detail, selectedFund, fundColor, cumPeriod]);

  // Drawdown area chart — inception to latest only
  const drawdownTrace = useMemo(() => {
    if (!detail?.drawdown) return null;
    const dd = parseDfJson(detail.drawdown);
    const pairs = dd.index.map((d, i) => {
      const v = Array.isArray(dd.data[i]) ? dd.data[i][0] : dd.data[i];
      return [new Date(d), v];
    }).filter(([, v]) => v != null);
    if (!pairs.length) return null;
    return {
      x: pairs.map(([d]) => d),
      y: pairs.map(([, v]) => v),
      type: 'scatter',
      mode: 'lines',
      fill: 'tozeroy',
      fillcolor: 'rgba(220, 53, 69, 0.2)',
      line: { color: '#dc3545', width: 1 },
      hovertemplate: 'Drawdown: %{y:.2%}<extra></extra>',
    };
  }, [detail]);

  // Distribution histogram
  const distributionTrace = useMemo(() => {
    if (!detail?.distribution) return null;
    const vals = Array.isArray(detail.distribution) ? detail.distribution
      : detail.distribution.values || detail.distribution.data || [];
    return {
      x: Array.isArray(vals[0]) ? vals.map(r => r[0]) : vals,
      type: 'histogram',
      nbinsx: 30,
      marker: { color: fundColor, opacity: 0.7 },
      hovertemplate: 'Return: %{x:.2%}<br>Count: %{y}<extra></extra>',
    };
  }, [detail, fundColor]);

  // Rolling 12M return — filter nulls so chart starts from fund inception
  const rollingTrace = useMemo(() => {
    if (!detail?.rolling_12m) return null;
    const r = parseDfJson(detail.rolling_12m);
    const allX = r.index.map(d => new Date(d));
    const allY = r.data.map(row => (Array.isArray(row) ? row[0] : row));
    // Filter to only non-null values
    const filtered = allX.reduce((acc, x, i) => {
      if (allY[i] != null && !isNaN(allY[i])) { acc.x.push(x); acc.y.push(allY[i]); }
      return acc;
    }, { x: [], y: [] });
    return {
      x: filtered.x,
      y: filtered.y,
      type: 'scatter',
      mode: 'lines',
      name: 'Rolling 12M',
      line: { color: fundColor, width: 2 },
      hovertemplate: '%{y:.2%}<extra></extra>',
    };
  }, [detail, fundColor]);

  // Monthly heatmap (year x month)
  const heatmapData = useMemo(() => {
    if (!detail?.monthly_heatmap) return null;
    const hm = detail.monthly_heatmap;

    // Expect {index: [years], columns: [months], data: [[values]]}
    const parsed = parseDfJson(hm);
    if (!parsed.index.length) return null;

    const years = parsed.index.map(String);
    const months = parsed.columns.length > 0 ? parsed.columns : MONTHS;

    return {
      z: parsed.data,
      x: months,
      y: years,
      type: 'heatmap',
      colorscale: HEATMAP_RETURNS,
      zmid: 0,
      text: parsed.data.map(row =>
        row.map(v => (v != null ? (v * 100).toFixed(1) + '%' : ''))
      ),
      texttemplate: '%{text}',
      hovertemplate: '%{y} %{x}: %{z:.2%}<extra></extra>',
      showscale: true,
      colorbar: { tickformat: '.0%', len: 0.8 },
    };
  }, [detail]);

  const selectCls = 'bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 text-sm text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-brand-gold/30 focus:border-brand-gold outline-none w-full';

  const [fundDropdownOpen, setFundDropdownOpen] = useState(false);
  const [fundSearch, setFundSearch] = useState('');
  const fundDropdownRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (fundDropdownRef.current && !fundDropdownRef.current.contains(e.target)) {
        setFundDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filteredFundList = fundSearch
    ? (fundList || []).filter(f => (f.display_name || f).toLowerCase().includes(fundSearch.toLowerCase()))
    : (fundList || []);

  return (
    <div>
      {/* Fund Selector */}
      <div className="mb-4 max-w-sm relative" ref={fundDropdownRef}>
        <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1 block">Select Fund</label>
        <button
          type="button"
          onClick={() => { setFundDropdownOpen(!fundDropdownOpen); setFundSearch(''); }}
          className={`${selectCls} text-left truncate`}
        >
          {selectedFund || 'Select a fund...'}
        </button>
        {fundDropdownOpen && (
          <div className="absolute z-50 mt-1 w-full bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-72 flex flex-col">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200 dark:border-gray-700">
              <MdSearch className="text-gray-400 shrink-0" />
              <input
                type="text"
                placeholder="Search funds..."
                value={fundSearch}
                onChange={(e) => setFundSearch(e.target.value)}
                className="flex-1 bg-transparent text-sm text-gray-800 dark:text-gray-200 outline-none placeholder-gray-400"
                autoFocus
              />
              {fundSearch && (
                <MdClose className="text-gray-400 cursor-pointer hover:text-gray-600" onClick={() => setFundSearch('')} />
              )}
            </div>
            <div className="overflow-y-auto flex-1">
              {filteredFundList.map((f) => {
                const name = f.display_name || f;
                const isSelected = name === selectedFund;
                return (
                  <div
                    key={name}
                    onClick={() => { setSelectedFund(name); setFundDropdownOpen(false); }}
                    className={`px-3 py-2 cursor-pointer text-sm truncate ${
                      isSelected
                        ? 'bg-brand-gold/10 text-brand-gold font-medium'
                        : 'text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                    }`}
                  >
                    {name}
                    {f.strategy && <span className="text-xs text-gray-400 ml-2">({f.strategy})</span>}
                  </div>
                );
              })}
              {filteredFundList.length === 0 && (
                <div className="px-3 py-3 text-sm text-gray-400 text-center">No funds match "{fundSearch}"</div>
              )}
            </div>
          </div>
        )}
      </div>

      {loading && <LoadingSpinner message={`Loading ${selectedFund} details...`} />}

      {error && (
        <div className="text-center py-12">
          <p className="text-red-500 mb-2">Failed to load fund detail</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{error}</p>
        </div>
      )}

      {!loading && !error && detail && (
        <>
          {/* Metric Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
            {metricCards.map((mc) => (
              <MetricCard key={mc.label} label={mc.label} value={mc.value} valueColor={mc.valueColor} />
            ))}
          </div>

          {/* Monthly Returns Bar Chart */}
          {monthlyBarTrace && (
            <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5 mb-4">
              <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-3">Monthly Returns</h3>
              <PlotlyChart
                data={[monthlyBarTrace]}
                layout={{
                  yaxis: { title: 'Monthly Return', tickformat: '.1%' },
                  xaxis: { title: '' },
                  showlegend: false,
                }}
              />
            </div>
          )}

          {/* Cumulative Return with SPX */}
          {cumulativeTraces.length > 0 && (
            <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5 mb-4">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200">Cumulative Return vs SPX</h3>
                <div className="flex gap-1">
                  {PERIOD_OPTIONS.map(p => (
                    <button
                      key={p.label}
                      onClick={() => setCumPeriod(p.months)}
                      className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                        cumPeriod === p.months
                          ? 'bg-brand-gold text-white'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              <PlotlyChart
                data={cumulativeTraces}
                layout={{
                  yaxis: { title: 'Cumulative Return', tickformat: '.0%' },
                  xaxis: {
                    title: '',
                    ...(cumulativeTraces[0]?.x?.length > 0 ? {
                      range: [cumulativeTraces[0].x[0], cumulativeTraces[0].x[cumulativeTraces[0].x.length - 1]],
                    } : {}),
                  },
                }}
                style={{ minHeight: 320 }}
              />
            </div>
          )}

          {/* Drawdown & Distribution side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            {/* Drawdown Chart */}
            {drawdownTrace && (
              <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5">
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-3">Drawdown</h3>
                <PlotlyChart
                  data={[drawdownTrace]}
                  layout={{
                    yaxis: { title: 'Drawdown', tickformat: '.0%' },
                    xaxis: { title: '' },
                    showlegend: false,
                  }}
                />
              </div>
            )}

            {/* Return Distribution */}
            {distributionTrace && (
              <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5">
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-3">Return Distribution</h3>
                <PlotlyChart
                  data={[distributionTrace]}
                  layout={{
                    yaxis: { title: 'Frequency', tickformat: 'd' },
                    xaxis: { title: 'Monthly Return', tickformat: '.1%' },
                    showlegend: false,
                  }}
                />
              </div>
            )}
          </div>

          {/* Rolling 12M Return */}
          {rollingTrace && (
            <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5 mb-4">
              <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-3">Rolling 12-Month Return</h3>
              <PlotlyChart
                data={[rollingTrace]}
                layout={{
                  yaxis: { title: 'Rolling 12M Return', tickformat: '.0%' },
                  xaxis: { title: '', ...(rollingTrace?.x?.length > 0 ? { range: [rollingTrace.x[0], rollingTrace.x[rollingTrace.x.length - 1]] } : {}) },
                  showlegend: false,
                  shapes: [{
                    type: 'line',
                    x0: 0, x1: 1, xref: 'paper',
                    y0: 0, y1: 0,
                    line: { color: '#999', width: 1, dash: 'dot' },
                  }],
                }}
              />
            </div>
          )}

          {/* Monthly Heatmap */}
          {heatmapData && (
            <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5 mb-4">
              <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-3">Monthly Returns Heatmap</h3>
              <div className="overflow-x-auto">
                <PlotlyChart
                  data={[heatmapData]}
                  interactive
                  layout={{
                    yaxis: { autorange: 'reversed', dtick: 1 },
                    xaxis: { side: 'top', dtick: 1 },
                    margin: { l: 60, r: 40, t: 40, b: 20 },
                  }}
                  style={{ minHeight: Math.max(350, (heatmapData.y?.length || 5) * 30 + 100) }}
                />
              </div>
            </div>
          )}

          {/* Fund Profile (terms, people, strategy, custom fields) */}
          {selectedFundId && <FundProfileSection fundId={selectedFundId} />}

          {/* Meeting notes */}
          {selectedFundId && <FundNotesSection fundId={selectedFundId} />}
        </>
      )}
    </div>
  );
};

export default FundDetailPage;
